import { motion } from 'framer-motion';
import { MoreVertical, ChefHat, Apple, Clock } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { fmtNum } from '@/utils/format';
import type { Favorite } from '@/types';

interface FavoriteCardProps {
  favorite: Favorite;
  onSelect?: (favorite: Favorite) => void;
  onActionsClick?: (favorite: Favorite) => void;
  showActions?: boolean;
}

/**
 * Карточка избранного блюда/продукта в списке.
 * 
 * Тап на карточку → onSelect (родитель открывает LogFavoriteModal)
 * Тап на "⋯" → onActionsClick (родитель показывает меню действий)
 */
export function FavoriteCard({
  favorite,
  onSelect,
  onActionsClick,
  showActions = true,
}: FavoriteCardProps) {
  const { haptic } = useTelegram();

  const isRecipe = favorite.is_recipe;

  // Основные цифры для отображения
  const displayCalories = isRecipe
    ? favorite.calories_per_portion ?? 0
    : favorite.calories_per_100g;

  const displayUnit = isRecipe ? favorite.portion_label ?? 'порция' : '100г';

  // Дата последнего использования (короткая)
  const lastUsedShort = formatLastUsed(favorite.last_used_at);

  function handleClick() {
    haptic('light');
    onSelect?.(favorite);
  }

  function handleActionsClick(e: React.MouseEvent) {
    e.stopPropagation();
    haptic('light');
    onActionsClick?.(favorite);
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileTap={{ scale: 0.98 }}
      className="w-full card p-3 flex items-center gap-3 text-left hover:bg-bg-elevated/40 transition-colors"
    >
      {/* Эмодзи */}
      <div className="w-12 h-12 rounded-xl bg-bg-elevated flex items-center justify-center text-2xl shrink-0">
        {favorite.emoji || (isRecipe ? '🍲' : '🍽')}
      </div>

      {/* Основная инфа */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <div className="font-display font-semibold text-sm text-ink truncate">
            {favorite.name}
          </div>
          {isRecipe ? (
            <ChefHat size={11} className="text-emerald-glow shrink-0" />
          ) : (
            <Apple size={11} className="text-sky-glow shrink-0" />
          )}
        </div>

        <div className="flex items-center gap-2 text-[11px] text-ink-dim">
          <span>
            {fmtNum(displayCalories)} ккал
            <span className="text-ink-dim/70 ml-1">/ {displayUnit}</span>
          </span>

          {isRecipe && favorite.total_portions && favorite.total_portions > 1 && (
            <>
              <span className="text-ink-dim/40">·</span>
              <span>{favorite.total_portions} порций</span>
            </>
          )}
        </div>

        {/* Метаданные использования */}
        {favorite.use_count > 0 && (
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-ink-dim/80">
            <Clock size={9} />
            <span>
              {favorite.use_count} {plural(favorite.use_count, 'раз', 'раза', 'раз')}
              {lastUsedShort && ` · ${lastUsedShort}`}
            </span>
          </div>
        )}
      </div>

      {/* Кнопка действий */}
      {showActions && onActionsClick && (
        <button
          type="button"
          onClick={handleActionsClick}
          className="w-9 h-9 rounded-lg text-ink-dim hover:text-ink hover:bg-bg-elevated/60 flex items-center justify-center shrink-0 transition-colors"
        >
          <MoreVertical size={16} />
        </button>
      )}
    </motion.button>
  );
}

// ============================================
// УТИЛИТЫ
// ============================================

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function formatLastUsed(iso?: string): string | null {
  if (!iso) return null;

  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays === 0) return 'сегодня';
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays} дн назад`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} нед назад`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} мес назад`;
  return `${Math.floor(diffDays / 365)} г назад`;
}
